import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bulkUpdateTasks,
  getGoalTree,
  listLabels,
  listMembers,
  getMe,
  type BulkPatch,
} from '../api';
import { useDismiss } from '../useDismiss';
import { useFocusOnOpen } from '../useFocusOnOpen';
import { useFitsViewport } from '../useFitsViewport';
import { useAccountDay } from '../accountDay';
import { shiftDay } from '../timezones';
import { Icon } from './Icon';
import { MultiSelect } from './MultiSelect';
import { Select } from './Select';
import { goalOptions, renderGoalOption } from './pickerOptions';

/**
 * The fields you cannot get at from a keystroke.
 *
 * The command bar already applies the five status verbs to a whole selection — those are one
 * keypress each and belong on the bar itself. Priority, due date, goals, labels and estimate need
 * a value chosen, which is a different interaction, and putting five more controls on the bar to
 * serve it would undo Phase 62's whole argument.
 *
 * Every field starts at "Keep". A multi-selection has no single current value for any of these, so
 * there is nothing honest to prefill, and an unset field must mean "leave it alone" rather than
 * "clear it" — otherwise opening this panel to set a priority silently wipes five due dates. Only
 * fields somebody touched are sent, which is also why `dueAt: null` has to be reachable explicitly:
 * "Clear" is a choice, absence is not.
 */

const PRIORITY_OPTIONS = [
  { value: '0', label: '—' },
  { value: '1', label: 'Low' },
  { value: '2', label: 'Medium' },
  { value: '3', label: 'High' },
];

const CLEAR = '__clear__';

export function BulkEdit({
  targets,
  onApplied,
  onDescribe,
  projectId,
}: {
  targets: string[];
  /** Fired after a successful write, so the list can drop the selection it just changed. */
  onApplied: () => void;
  /*
    What the patch changed, in words, so the list can print a readback.

    Apply is the only control on the selection bar that said nothing. The verbs beside it announce
    themselves and offer Reopen; Apply rewrote dates, labels, goals and the assignee across the
    whole selection and cleared it silently — the most destructive control with the least to say.
  */
  onDescribe?: (what: string, count: number) => void;
  /**
   * The project the list is scoped to, if any.
   *
   * Assignment is offered only here, because it only means anything here: `setAssignee` refuses to
   * hand a task outside a project to anybody but its own author, so unscoped there is nobody to
   * choose between. Scoped, the project's members are the choices.
   */
  projectId: string | null;
}) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  /*
    Anchored to the trigger, but never off the side of the window.

    `right: 0` alone put this panel — and its Apply button with it — outside the left edge of a
    375px screen whenever the command bar wrapped the Edit… trigger short of the panel's own width.
    See `useFitsViewport` for why no overflow check ever noticed.
  */
  const panel = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const [priority, setPriority] = useState<string | null>(null);
  const [due, setDue] = useState<string>('');
  /** Days from today to plan the selection for, or null to leave the schedule alone. */
  const [planIn, setPlanIn] = useState<number | null>(null);
  const [clearDue, setClearDue] = useState(false);
  const [goalIds, setGoalIds] = useState<string[]>([]);
  const [touchGoals, setTouchGoals] = useState(false);
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [touchLabels, setTouchLabels] = useState(false);
  const [estimate, setEstimate] = useState<string>('');
  /** `''` keeps the assignee, `CLEAR` releases, anything else is a user id — see `patch` below. */
  const [assignee, setAssignee] = useState<string>('');

  /*
    The house dismissal contract, rather than a fourth hand-rolled copy of it.

    This used to be its own pointerdown-and-Escape pair, which behaved identically and was invisible
    to everything else — and that is what made one Escape close this popover *and* wipe the
    selection it was about to edit. The task list clears its selection from a `document` handler and
    had no way to know a surface had just closed; registering here puts this panel on the stack
    `hasOpenSurface` reads, so the list stands down for the press that dismissed this.
  */
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, [wrapper]);

  useFitsViewport(open, panel);

  /*
    Focus goes in, stays in, and comes back to *Edit…*.

    It did none of the three: the dialog opened with focus still on the trigger, and the systems
    architect's Tab trail left it after thirteen presses for a task title in the list behind. Now
    that Enter on a focused *Edit…* opens it (`focusOwnsKey`), a keyboard user reaches it for the
    first time, so it has to be usable from there. Focus lands on the first field and Tab cycles
    the fields; Escape (through `useDismiss`) closes it and hands focus back to the trigger.

    After a successful Apply the selection is cleared and the bar — trigger and all — unmounts, so
    there is no *Edit…* to return to. Focus goes to the row under the cursor instead: the place
    `j`/`k` are already pointing, rather than `<body>`, where the next Tab starts at the top nav.
  */
  const trigger = useRef<HTMLButtonElement>(null);
  useFocusOnOpen(open, panel, {
    trap: true,
    returnTo: () =>
      trigger.current?.isConnected
        ? trigger.current
        : document.querySelector<HTMLElement>(
            '.task-row.is-cursor .task-title, .board-card[data-cursor] .board-card-title',
          ),
  });

  const goals = useQuery({ queryKey: ['goals', 'tree'], queryFn: getGoalTree, enabled: open });
  const me = useQuery({ queryKey: ['me'], queryFn: getMe });
  const { today } = useAccountDay();
  const members = useQuery({
    queryKey: ['members', projectId],
    queryFn: () => listMembers(projectId!),
    enabled: open && projectId !== null,
  });
  const labels = useQuery({ queryKey: ['labels'], queryFn: listLabels, enabled: open });

  const goalChoices = useMemo(() => goalOptions(goals.data ?? []), [goals.data]);
  const labelChoices = useMemo(
    () => (labels.data ?? []).map((l) => ({ value: l.id, label: l.name })),
    [labels.data],
  );

  const patch: BulkPatch = {};
  if (priority !== null) patch.priority = Number(priority);
  if (clearDue) patch.dueAt = null;
  /*
    Counted from the account's day. This assembled the browser's out of `getFullYear`/`getMonth`/
    `getDate`, so "today" on a laptop a day ahead of the profile planned thirty tasks for tomorrow
    in one press — the same defect as the row menu's, done in bulk.
  */
  if (planIn !== null) patch.scheduledDate = shiftDay(today, planIn);
  else if (due !== '') patch.dueAt = new Date(`${due}T12:00:00`).toISOString();
  if (touchGoals) patch.goalIds = goalIds;
  if (touchLabels) patch.labelIds = labelIds;
  if (estimate === CLEAR) patch.estimateMinutes = null;
  else if (estimate !== '') patch.estimateMinutes = Number(estimate);
  if (assignee === CLEAR) patch.assigneeUserId = null;
  else if (assignee !== '') patch.assigneeUserId = assignee;

  const fieldCount = Object.keys(patch).length;

  /*
    The patch as a sentence. Named fields rather than a count, because "changed 3 tasks" tells you
    nothing about what to look at if it was wrong.
  */
  function describePatch(p: BulkPatch): string {
    const parts: string[] = [];
    const f = p as Record<string, unknown>;
    if (f.scheduledDate !== undefined) parts.push('the plan date');
    if (f.dueAt !== undefined) parts.push('the deadline');
    if (f.priority !== undefined) parts.push('priority');
    if (f.labelIds !== undefined) parts.push('labels');
    if (f.goalIds !== undefined) parts.push('goals');
    if (f.assigneeUserId !== undefined) parts.push('the assignee');
    if (f.projectId !== undefined) parts.push('the project');
    if (parts.length === 0) return 'nothing';
    if (parts.length === 1) return parts[0]!;
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  }

  const apply = useMutation({
    mutationFn: () => bulkUpdateTasks(targets, patch),
    onSuccess: (result) => {
      void queryClient.invalidateQueries();
      // Deliberately not closed on a partial write: the count is the only place the user learns
      // that some of what they selected did not take.
      if (result.skipped.length === 0) {
        onDescribe?.(describePatch(patch), targets.length);
        setOpen(false);
        reset();
        onApplied();
      }
    },
  });

  function reset() {
    setPriority(null);
    setDue('');
    setClearDue(false);
    setPlanIn(null);
    setGoalIds([]);
    setTouchGoals(false);
    setLabelIds([]);
    setTouchLabels(false);
    setEstimate('');
    setAssignee('');
    apply.reset();
  }

  return (
    <div className="bulk-edit" ref={wrapper}>
      <button
        ref={trigger}
        type="button"
        className="command"
        disabled={targets.length === 0}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="edit" />
        {/* Plain text, like the other commands — see the note in `TaskCommands`. */}
        Edit…
      </button>

      {open && (
        <div
          ref={panel}
          className="bulk-edit-panel card"
          role="dialog"
          aria-label="Edit selected tasks"
          tabIndex={-1}
        >
          <p className="subtext">
            Applies to <strong>{targets.length}</strong> {targets.length === 1 ? 'task' : 'tasks'}.
            Anything left on Keep is untouched.
          </p>

          {/*
            Assignment, which this could not do — in the release whose headline feature it was.

            Two personas noted it independently. Offered only when the list is scoped to a project,
            because that is the only place it means anything: `setAssignee` refuses to hand a task
            outside a project to anyone but its own author, so unscoped there is nobody to choose
            between and an empty picker would be worse than none.
          */}
          {projectId !== null && (
            <label className="bulk-field">
              <span>Assignee</span>
              <Select
                options={[
                  { value: CLEAR, label: 'Nobody (unclaim)' },
                  ...(members.data ?? [])
                    .filter((m) => m.acceptedAt !== null)
                    .map((m) => ({
                      value: m.userId,
                      label:
                        m.userId === me.data?.id
                          ? `${m.displayName || m.email} (you)`
                          : m.displayName || m.email,
                    })),
                ]}
                value={assignee === '' ? null : assignee}
                onChange={(next) => setAssignee(next ?? '')}
                ariaLabel="Set assignee"
                placeholder="Keep"
                emptyLabel="Keep"
              />
            </label>
          )}

          <label className="bulk-field">
            <span>Priority</span>
            <Select
              options={PRIORITY_OPTIONS}
              value={priority}
              onChange={setPriority}
              ariaLabel="Set priority"
              placeholder="Keep"
              emptyLabel="Keep"
            />
          </label>

          <label className="bulk-field">
            <span>Due</span>
            <input
              type="date"
              value={due}
              disabled={clearDue}
              aria-label="Set due date"
              onChange={(e) => setDue(e.target.value)}
            />
          </label>

          {/*
            Plan for, not due by.

            Deferring is a statement about when you intend to *do* something, which is
            `scheduledDate` — the deadline is a different fact and pushing one is not pushing the
            other. The three relative buttons are the whole point: with thirty rows selected,
            typing a date thirty times is the thing this panel exists to avoid.
          */}
          <div className="bulk-field">
            <span>Plan for</span>
            <div className="row gap">
              {(
                [
                  ['Tomorrow', 1],
                  ['Next week', 7],
                  ['Next month', 30],
                ] as const
              ).map(([label, days]) => (
                <button
                  key={label}
                  type="button"
                  className={planIn === days ? 'is-active' : ''}
                  aria-pressed={planIn === days}
                  onClick={() => setPlanIn(planIn === days ? null : days)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <label className="bulk-check">
            <input
              type="checkbox"
              checked={clearDue}
              onChange={(e) => {
                setClearDue(e.target.checked);
                if (e.target.checked) setDue('');
              }}
            />
            <span>Clear the due date</span>
          </label>

          {goalChoices.length > 0 && (
            <label className="bulk-field">
              <span>Goals</span>
              <MultiSelect
                options={goalChoices}
                renderOption={renderGoalOption}
                value={goalIds}
                onChange={(next) => {
                  setGoalIds(next);
                  setTouchGoals(true);
                }}
                ariaLabel="Set goals"
                placeholder="Keep"
                dialogTitle="Goals"
              />
            </label>
          )}

          {labelChoices.length > 0 && (
            <label className="bulk-field">
              <span>Labels</span>
              <MultiSelect
                options={labelChoices}
                value={labelIds}
                onChange={(next) => {
                  setLabelIds(next);
                  setTouchLabels(true);
                }}
                ariaLabel="Set labels"
                placeholder="Keep"
                dialogTitle="Labels"
              />
            </label>
          )}

          <label className="bulk-field">
            <span>Estimate</span>
            <Select
              options={[
                { value: '15', label: '15m' },
                { value: '30', label: '30m' },
                { value: '60', label: '1h' },
                { value: '120', label: '2h' },
                { value: CLEAR, label: 'Clear' },
              ]}
              value={estimate === '' ? null : estimate}
              onChange={(next) => setEstimate(next ?? '')}
              ariaLabel="Set estimate"
              placeholder="Keep"
              emptyLabel="Keep"
            />
          </label>

          {apply.data && apply.data.skipped.length > 0 && (
            <p className="subtext" role="status">
              {apply.data.updated} updated, {apply.data.skipped.length} skipped.
            </p>
          )}
          {apply.isError && (
            <p className="pill bad" role="status">
              {(apply.error as Error).message}
            </p>
          )}

          <div className="bulk-actions">
            <button
              type="button"
              className="btn primary"
              disabled={fieldCount === 0 || apply.isPending}
              onClick={() => apply.mutate()}
            >
              {apply.isPending ? 'Applying…' : `Apply${fieldCount > 0 ? ` ${fieldCount}` : ''}`}
            </button>
            <button type="button" className="link" onClick={reset}>
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
